import { useEffect, useState } from "react";
import { Ban, Icon, Pencil, Trash2, EyeIcon } from "lucide-react";
import { PlusIcon } from '@heroicons/react/20/solid'
import { BankAccountModels } from '../models/bank-accounts';
import { BankAccountDialog } from "./bank-accounts-form-dialog";
import useApi from "src/configs/api";
import { Dialog } from "src/components/ui/dialog";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { useAlert } from "src/hooks/use-alert";
import { useLoader, LoaderProvider } from "src/contexts/LoaderContext";
import { FormMode } from "src/models";

function classNames(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

const BudgetPlannerSettings = () => {
    const { showWarning, showSuccess    , showError} = useAlert()
    const {showLoader, hideLoader} = useLoader();
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState<FormMode>("add");
    const [bankAccounts, setBankAccounts] = useState<BankAccountModels.BankAccountList>([]);
    const [selectedBankAccount, setSelectedBankAccounts] = useState<BankAccountModels.BankAccountDetails | null>(null);
    const [showBankAccountDialog, setShowBankAccountDialog] = useState(false);
    
    const api = useApi();

    useEffect(() => { 
        fetchBankAccounts();
     }, []);

    const fetchBankAccounts = async () => {
        try {
            const res = await api.get("/finance/bank-accounts");
            setBankAccounts(res.data.data);
        }
        catch (error) {console.error("Failed to fetch user details:", error);}
    }

    const handleRefresh = () => {
        fetchBankAccounts();               // reload data
        setShowBankAccountDialog(false);   // close dialog
        setSelectedBankAccounts(null);     // ✅ reset selected data
        setMode("add");                    // ✅ reset mode (important)
    };

    const fetchSelectedAccountDetails = async (accountId : number) => {
        const res = await api.get(`/finance/bank-accounts/${accountId}`);
        if (res.status === 200) {
            const result = res.data.data;
            setSelectedBankAccounts(result);
        }        
        return res;
    }
    
    
    const handleCreate = async () => {
        setSelectedBankAccounts(null);
        setMode("add");
        setShowBankAccountDialog(true);
    }
    
    const handleView = async (accountId: number) => {
        showLoader();
        try
        {
            await fetchSelectedAccountDetails(accountId);
            setMode("view")
            setShowBankAccountDialog(true);
        }
        catch(error)
        {
            // setShowBankAccountDialog(false);
            showError("", "An error occured while trying to proceed your request")
        }

        hideLoader();
    }

    const handleDialogChange = (open: boolean) => {
    setShowBankAccountDialog(open);

    if (!open) {
        setSelectedBankAccounts(null);
        setMode("add");
    }
};

    const handleEdit = async (accountId: number) => {
        showLoader();
        try
        {
            await fetchSelectedAccountDetails(accountId);
            setMode("edit")
            setShowBankAccountDialog(true);
        }
        catch(error)
        {
            setShowBankAccountDialog(false);
            showError("", "An error occured while trying to proceed your request")
        }
        hideLoader();
    }

    const handleDelete = async (accountId: number) => {
        
        showWarning(
            "Delete Account Confirmation", 
            "This request can not be undone",
            {
                label : "Delete",
                onClick : async () => {
                    try
                    {
                        await api.delete(`/finance/bank-accounts/${accountId}`);
                        showSuccess("Deleted");
                        fetchBankAccounts();                        
                    }
                    catch {
                        showError("", "An error occured while trying to proceed your request.")
                    }
                }
            }
        )
    }

    

    return (
        <>
        <div className="text-sm ">            
            <div className="bg-sidebar p-5 rounded-md">
                <div className="border-b border-gray-200 py-2 ">
                    <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
                        <div className="ml-4 mt-2">
                        <h3 className="text-base font-semibold leading-6 text-gray-900">BANK ACCOUNTS</h3>
                        </div>
                        <div className="ml-4 mt-2 flex-shrink-0">
                            <button
                                onClick={() => handleCreate()}
                                type="button"
                                className="relative inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50" title="Add New Bank Account"
                            >
                                <PlusIcon className="size-4 md:size-3 tex-gray-400" />
                                <span className="hidden md:inline ml-1">New Account</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-5 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        {bankAccounts ? (

                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <table className="min-w-full divide-y divide-gray-300 mx-2">
                                <thead>
                                    <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                        Name
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell">
                                        Number
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Type
                                    </th>
                                    
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {bankAccounts.map((account) => (
                                    <tr key={account.accountId} className="hover:bg-gray-50">

                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                            <button onClick={() => handleView(account.accountId)} className="relative group font-semibold text-sm text-blue-600 hover:text-blue-900 hover:underline">
                                                {account.accountName}
                                                
                                            </button>
                                        </td>
                                        
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 hidden md:table-cell">{account.accountNumber ?? "-"}</td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{account.accountType}</td>
                                        <td className="relative flex whitespace-nowrap gap-1 md:gap-2 py-4 pl-3 pr-2 text-right text-sm font-medium sm:pr-0">
                                            
                                            {/* <Dialog open={showBankAccountDialog} onOpenChange={setShowBankAccountDialog}> */}
                                                {/* <DialogTrigger asChild> */}
                                                <button onClick={() => handleEdit(account.accountId)} className="relative group text-sm text-gray-600 hover:text-blue-600">
                                                    <Pencil className="size-4 text-gray-600 hover:text-blue-600 cursor-pointer" />
                                                    <span className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded">
                                                    Edit
                                                    </span>
                                                </button>

                                            <button onClick={() => handleDelete(account.accountId)} className="relative group text-sm text-gray-600 hover:text-red-900">
                                                <Trash2 className="size-4" />
                                                <span className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded">
                                                Delete
                                                </span>
                                            </button>
                                        </td>
                                    </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>) : (
                        <span className="text-center py-4 italic text-gray-500 inline-block min-w-full align-middle sm:px-6 lg:px-8">
                            No accounts.
                        </span>
                        )
                    } 
                    </div>
                </div>
            </div>
        </div>

        <Dialog open={showBankAccountDialog} onOpenChange={handleDialogChange}>
            <BankAccountDialog
                mode={mode}
                data={selectedBankAccount}
                onSubmit={handleRefresh}
                onOpenChange={setShowBankAccountDialog}
            />
        </Dialog>

        
        </>
    )
}

export default BudgetPlannerSettings;